// @flow
import * as React from "react";
import {observable, runInAction, useStrict} from "mobx";
useStrict(true);

import Api from "../api/index.js";
import {processMenues} from "../api/user.js";
import type {Content} from "../api/types.js";
import type {JSONContent} from "../api/jsontypes.js";
import Options from "./options.js";
import type {InitOptions, Viewport} from "./options.js";
import UrlResolver, {isEmbedded} from "./urlresolver.js";
import type {To, Location, Action} from "./urlresolver.js";
import DataResolver from "./dataresolver.js";

import initStyle from "../styling/index.js";

import ContentError from "../screens/error.js";
import LoadingScreen from "../screens/loading.js";

export type {InitOptions as Options};
export type State = Array<JSONContent>;
export type {Location};

export {isEmbedded};

export type PlayerOptions = {
	viewport?: Viewport;
	navigateTo?: (path: string, replace: boolean) => void;
};

class Player {
	api: Api;
	options: Options;
	user: ?any;

	_navigateTo: (path: string, replace: boolean) => void;
	_nextUrl: UrlResolver;

	@observable.ref url: UrlResolver;
	@observable back: string;
	@observable.ref content: ?Content;
	@observable.ref element: React.Element<*>;

	/**
	 * @param {InitOptions} options - The options for the player
	 * @param {State} state - An Array of content descriptors
	 * @param {Object} playerOptions - additional player options
	 * @param {Viewport} playerOptions.viewport - The available viewport size
	 * @param {function} playerOptions.navigatoTo - The handler function for navigation
	 *                                              requests
	 */
	constructor(options: InitOptions, state: State, {viewport, navigateTo}: PlayerOptions = {}) {
		const _viewport = viewport || options.viewport || {width: 1024, height: 768};
		this.user = options.user;
		this.options = new Options(options, _viewport);
		if (navigateTo) {
			this._navigateTo = (...args) => {
				if (
					args &&
					args[0] &&
					args[0].startsWith &&
					this.options.historyBaseName &&
					args[0].startsWith(this.options.historyBaseName)
				) {
					args[0] = '/' + args[0].substring(this.options.historyBaseName.length);
				}

				navigateTo(...args);
			}
		}
		runInAction("constructor", () => {
			const api = this.api = new Api(this.options);
			if (this.options.menues) {
				this.options.menues = processMenues(api, this.options.menues);
			}
			if (!state) { return; }
			for (const content of Array.isArray(state) ? state : [state]) {
				api.register(content);
			}
		}, this);

		initStyle(options);
	}

	/**
	 * Convenience function to trigger a navigation to the results of the given search
	 * @param {string} search - The string to search for
	 */
	performSearch(search: string) {
		this.navigateTo(this.url.create({search}));
	}

	/**
	 * Triggers a navigation to the given target
	 * @param {To} to - The target to navigate to
	 * @param {boolean} replace - If true the current state in the history is replace
	 *                            otherwise a new step is added to the history
	 */
	navigateTo(to: To, replace: boolean = false) {
		// silently fail in case we can not do navigation
		if (!this._navigateTo) { return; }
		to = this.url.create(to, {absolute: false});
		//only navigate if url is different
		if (to !== this.url.url) {
			this._navigateTo(to, replace);
		}	
	}

  /**
   * Handles the navigation to the given location
   * @param {Location} location - The location to navigate to
   * @param {Action=} action - The navigation action
   */
	async handleNavigation(location: Location, action?: Action) {
		const url = new UrlResolver(this.options, this.api, location, action);

		// We need to check the url here, otherwise we could have async races.
		// What a nice thing that Promises are not cancelable.
		if (this.url && this.url.url === url.url) { return; }
		this._nextUrl = url;

		if (url.locale && this.options.locale !== url.locale) {
			runInAction("handleNavigation", () => {
				if (this.publisher && this.publisher.id) {
					this.api.clearUser(this.publisher.id);
				}
				this.element = (<LoadingScreen />);
				this.options.locale = url.locale;
			}, this);
		}

		if (this.options.isEmbedded && url.url === '/' && this.url && !this.url.hierarchy.length) {
			window.parent.postMessage({type: "kent-closing"}, "*");
			if (this.url) {
				return;
			}
		}

		const data = new DataResolver(this.options, this.api, url);
		const _url = url.url;
		let result;
		try {
			result = await data.resolve();
		} catch (err) {
			// XXX: this is a reaaaaly dirty hack :-(
			if (this.options.isEmbedded && err.message === "no item to resolve") {
				window.parent.postMessage({type: "kent-closing"}, "*");
				if (this.url) {
					return;
				}
			}
			if (this.options.log) {
				console.error(err);
			}
			result = {
				content: err.content || null,
				back: url.create({hierarchy: []}),
				element: (<ContentError type={err.type || 418} message={err.message} />),
			};
		}
		if (this._nextUrl !== url) { return; }
		runInAction("handleNavigation", () => {
			this.options.locale = url.locale;
			this.url = url;
			Object.assign(this, result);
		}, this);
		// We hit a redirect. Update the browsers urlbar here, since `navigateTo`
		// is synchronous and would otherwise mess up our current navigation.
		if (url.url !== _url) {
			this.navigateTo(url.url, true);
		}
	}
}

export default Player;



// WEBPACK FOOTER //
// ./lib/player/player.js